import { isPlainObject } from 'shared/utils/object';
import type { DocumentationSignedUrls } from './documentationContract';

const OPTIONAL_URL_KEYS = [
  'imageSignedRequest',
  'thumbnailSignedRequest',
  'mergedSignedRequest',
  'mergedThumbnailSignedRequest',
  'drawingSignedRequest',
] as const;

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function readField(value: unknown, key: string): unknown {
  return isPlainObject(value) ? value[key] : undefined;
}

/** The server sends the rotation of the version at one of several places. */
function readImageRotation(body: Record<string, unknown>): number | undefined {
  const { document, version } = body;
  const candidates = [
    readField(document, 'imageRotation'),
    readField(readField(document, 'data'), 'imageRotation'),
    readField(readField(document, 'version'), 'imageRotation'),
    body.imageRotation,
    readField(version, 'imageRotation'),
  ];
  return candidates.map(toFiniteNumber).find((rotation) => rotation !== undefined);
}

/**
 * The signed URLs of a documentation version from the server's answer, or
 * `undefined` when it has no main URL.
 */
export function readDocumentationSignedUrls(body: unknown): DocumentationSignedUrls | undefined {
  if (!isPlainObject(body) || typeof body.signedRequest !== 'string') {
    return undefined;
  }

  const urls: DocumentationSignedUrls = { signedRequest: body.signedRequest };
  OPTIONAL_URL_KEYS.forEach((key) => {
    const url = body[key];
    if (typeof url === 'string' && url) {
      urls[key] = url;
    }
  });
  const imageRotation = readImageRotation(body);
  if (imageRotation !== undefined) {
    urls.imageRotation = imageRotation;
  }
  return urls;
}
